import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GridFilterItem, GridFilterModel } from '../lib/dataGrid';

/**
 * Opening a grid already filtered, from a link: to a day, or to a Befund.
 *
 * The Tagebuch points at everything recorded on its day — the Zeiterfassung,
 * the Befunde, Funde, Zeichenblätter, Fotos and Gesamtbeschreibungen written up
 * on it. Every one of those is the same move: read `?date=YYYY-MM-DD` off the
 * URL and seed the grid's filter with it.
 */

/** The query parameter every date-filtered grid link uses. */
export const DATE_LINK_PARAM = 'date';

/**
 * A filter item for a `date` column, seeded from `?date=` — or none when the
 * parameter is absent or malformed.
 *
 * THE VALUE MUST BE A DATE AT UTC MIDNIGHT. That is what MUI's own date input
 * produces (`new Date('2024-07-10')`), and both halves of the grid are written
 * for it: the filter re-adds the timezone offset before comparing, and the
 * panel renders the value back out with `toISOString()`. Hand it LOCAL midnight
 * and east of Greenwich both land a day early.
 */
export function useDateLinkFilterItems(field: string): GridFilterItem[] {
    const [searchParams] = useSearchParams();
    const date = searchParams.get(DATE_LINK_PARAM) ?? '';
    return useMemo(() => {
        // Matched, not parsed: the value comes off a URL, and only the stored
        // 'YYYY-MM-DD' shape may be handed to the Date below.
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return [];
        }
        return [
            {
                id: 'linked-date',
                field,
                operator: 'is',
                value: new Date(`${date}T00:00:00Z`)
            }
        ];
    }, [date, field]);
}

/**
 * The whole filter model for a grid whose only deep link is the day — spread
 * into `initialState.filter`. SEEDED, not controlled: the filter panel stays
 * the user's from here on, so they can widen or drop what the link set.
 */
export function useDateLinkFilter(field: string): GridFilterModel {
    const items = useDateLinkFilterItems(field);
    return useMemo(() => ({ items }), [items]);
}

/** The link a grid page is reached by, filtered to one day. */
export function dateLinkPath(basePath: string, date: string): string {
    return `${basePath}?${DATE_LINK_PARAM}=${date}`;
}

/** The query parameter a link to the records of one Befund uses. */
export const FINDING_LINK_PARAM = 'finding';

/**
 * A filter item for a grid's Befundnummer column, seeded from `?finding=` — or
 * none when the parameter is absent or the grid names no such column.
 *
 * The column is the hidden `multiSelect` one every Befund-referencing grid
 * carries (see useFindingRefFilterColumns): it holds the Befunde a record
 * reaches directly AND through a Planum or Profil, so the link finds both.
 */
export function useFindingLinkFilterItems(
    field: string | undefined
): GridFilterItem[] {
    const [searchParams] = useSearchParams();
    // Untrimmed: the column holds the numbers as stored.
    const findingNumber = searchParams.get(FINDING_LINK_PARAM) ?? '';
    return useMemo(() => {
        if (!field || !findingNumber.trim()) {
            return [];
        }
        return [
            {
                id: 'linked-finding',
                field,
                operator: 'containsAnyOf',
                value: [findingNumber]
            }
        ];
    }, [field, findingNumber]);
}

/** The link a grid page is reached by, filtered to one Befund's records. */
export function findingLinkPath(
    basePath: string,
    findingNumber: string
): string {
    return `${basePath}?${FINDING_LINK_PARAM}=${encodeURIComponent(findingNumber)}`;
}
